import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function RunStatusBadge({ status }) {
  const cls =
    status === 'succeeded'
      ? 'bg-hxd-ok'
      : status === 'failed'
      ? 'bg-hxd-err'
      : status === 'running'
      ? 'bg-hxd-warn'
      : status === 'cancelled'
      ? 'bg-hxd-subtle'
      : 'bg-hxd-muted';
  return (
    <span
      className={`${cls} text-white font-display text-[9px] font-bold uppercase tracking-display-sm px-1.5 py-0.5 rounded-[2px]`}
    >
      {status}
    </span>
  );
}

export default function RunDetail() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStack, setShowStack] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.get(`/dev/pipeline/runs/${id}`);
        if (!cancelled) setRun(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <p className="text-hxd-muted text-[13px]">Loading run…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <Link to="/admin/dev/runs" className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors">
          ← Back to runs
        </Link>
        <p className="text-hxd-err text-[13px] mt-4">{error}</p>
      </div>
    );
  }

  const promptEntries = Object.entries(run.promptVersions ?? {});
  const stageEntries = run.stages ? Object.entries(run.stages) : [];

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/admin/dev/runs"
        className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors"
      >
        ← Back to runs
      </Link>

      <div className="mt-6 space-y-5">
        {/* Header card */}
        <div className="hxd-card">
          <div className="hxd-card-header">Run Summary</div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
            <div>
              <dt className="hxd-label">Status</dt>
              <dd className="mt-1">
                <RunStatusBadge status={run.status} />
              </dd>
            </div>
            <div>
              <dt className="hxd-label">Run ID</dt>
              <dd className="mt-1 font-mono text-[12px] text-hxd-ink break-all">{run._id}</dd>
            </div>
            <div>
              <dt className="hxd-label">Requested At</dt>
              <dd className="mt-1 text-hxd-muted">{fmt(run.requestedAt)}</dd>
            </div>
            <div>
              <dt className="hxd-label">Finished At</dt>
              <dd className="mt-1 text-hxd-muted">{fmt(run.finishedAt)}</dd>
            </div>
            {run.startedAt && (
              <div>
                <dt className="hxd-label">Started At</dt>
                <dd className="mt-1 text-hxd-muted">{fmt(run.startedAt)}</dd>
              </div>
            )}
            <div>
              <dt className="hxd-label">Requested By</dt>
              <dd className="mt-1 font-mono text-[12px] text-hxd-muted">
                {run.requestedBy ?? '—'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Snapshot */}
        <div className="hxd-card">
          <div className="hxd-card-header">Run Snapshot</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <p className="hxd-label mb-2">Prompt Versions</p>
              {promptEntries.length === 0 ? (
                <p className="text-hxd-muted text-[13px]">—</p>
              ) : (
                <table className="text-[12px] w-full">
                  <tbody>
                    {promptEntries.map(([name, version]) => (
                      <tr key={name} className="border-b border-hxd-border last:border-0">
                        <td className="py-1.5 pr-4 font-mono text-hxd-ink">{name}</td>
                        <td className="py-1.5 text-hxd-muted">v{version}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <p className="hxd-label mb-2">Source Count</p>
              <p className="text-[13px] text-hxd-ink">
                {run.sourceCount ?? '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Error card */}
        {run.status === 'failed' && (
          <div className="hxd-card border-red-200 bg-red-50">
            <div className="hxd-card-header">Error</div>
            <p className="text-hxd-err text-[13px] mb-3">
              {run.errorMessage ?? 'No error message recorded.'}
            </p>
            {run.errorStack && (
              <>
                <button
                  type="button"
                  className="hxd-btn hxd-btn-ghost hxd-btn-sm text-[12px]"
                  onClick={() => setShowStack((v) => !v)}
                >
                  {showStack ? 'Hide stack trace' : 'Show stack trace'}
                </button>
                {showStack && (
                  <pre className="mt-3 text-[11px] font-mono whitespace-pre-wrap break-words text-hxd-ink bg-white border border-red-100 rounded p-3 max-h-64 overflow-y-auto">
                    {run.errorStack}
                  </pre>
                )}
              </>
            )}
          </div>
        )}

        {/* Success details */}
        {run.status === 'succeeded' && (
          <div className="hxd-card">
            <div className="hxd-card-header">Output</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
              <div>
                <dt className="hxd-label">Brief ID</dt>
                <dd className="mt-1 font-mono text-[12px] text-hxd-ink">{run.briefId ?? '—'}</dd>
              </div>
              <div>
                <dt className="hxd-label">Edition Date</dt>
                <dd className="mt-1 text-hxd-muted">{run.editionIsoDate ?? '—'}</dd>
              </div>
            </dl>
          </div>
        )}

        {/* Stages */}
        <div className="hxd-card">
          <div className="hxd-card-header">Stage Outputs</div>
          {stageEntries.length === 0 ? (
            <p className="text-hxd-muted text-[13px]">
              No stage outputs (run did not produce stage data).
            </p>
          ) : (
            <ul className="space-y-2">
              {stageEntries.map(([stage]) => (
                <li key={stage} className="flex items-center justify-between gap-4 text-[13px]">
                  <span className="font-mono text-hxd-ink">{stage}</span>
                  <a
                    href={`${API_BASE}/dev/pipeline/runs/${id}/stage/${stage}/download`}
                    download
                    className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                  >
                    Download JSON
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
